import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { NavigationService } from '@treasury/core/navigation';
import { DownloadService } from '@treasury/domain/download';
import { Entitlements, EntitlementsService } from '@treasury/domain/entitlements';
import { TmApiError } from '@treasury/domain/shared';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { WireUploadFormat, WiresService } from '@treasury/domain/wires';
import {
    type FieldsArray,
    type WireUploadFormatUIModelReturn,
} from '@treasury/domain/wires/mappings/wire-upload-formats.mappings';
import { TmContainer, TmFooterService } from '@treasury/presentation';
import '@treasury/presentation/layouts/file-format-designer';
import { fontAwesome } from '@treasury/presentation/layouts/font-awesome';
import { TmDialogButtonPosture, TmDialogService } from '@treasury/presentation/services/dialog';
import { css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { HeaderBarService } from '../../services/jhd-header-bar.service';
import { defaultFormat } from '../data/default-wire-upload-format';

export const tagName = 'wire-upload-format-container';

@customElement(tagName)
export class WireUploadFormatContainer extends TmContainer {
    @InjectProperty()
    declare private readonly headerService: HeaderBarService;

    @InjectProperty()
    declare private readonly footerService: TmFooterService;

    @InjectProperty()
    declare private readonly entitlementsService: EntitlementsService;

    @InjectProperty()
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    declare private readonly downloadService: DownloadService<any>;

    @InjectProperty()
    declare private readonly wiresService: WiresService;

    @InjectProperty()
    declare private dialogService: TmDialogService;

    @InjectProperty()
    declare private readonly navService: NavigationService;

    @state()
    private hasWireFileUpload = false;

    @state()
    private formatId = '';

    @state()
    private entitlement = false;

    @state()
    private format: WireUploadFormatUIModelReturn = {} as WireUploadFormatUIModelReturn;

    @state()
    private formatFields: FieldsArray = [];

    @state()
    private glossary: Array<{
        title: string;
        label: string;
        id: string;
        terms: Array<{
            name: string;
            text: string;
        }>;
    }> = [];

    protected async firstUpdated() {
        this.loading = true;
        this.headerService.configure({
            title: 'Wire File Upload Format Designer',
            backAction: () => this.navService.navigate(`wires`),
        });
        const { params } = await this.navService.getRouteData<{ id: string }>();
        this.formatId = params.id;
        await this.getWireUploadFormatData();
    }

    protected render() {
        return html`<tm-section
            class=${classMap({
                'flex flex-col': true,
                'overflow-hidden h-full bg-page': !this.isDesktop,
                'mx-2 mb-4 pb-2 rounded-lg': this.isDesktop,
            })}
            >${this.loading
                ? html`<tm-loader card></tm-loader>`
                : html`<tm-file-format-designer
                      .format=${this.format}
                      .glossary=${this.glossary}
                      .entitlement=${this.entitlement}
                      .loading=${this.loading}
                      @save=${(e: CustomEvent) => {
                          this.format = e.detail.format;
                          this.saveFormat();
                      }}
                      @cancel=${() => {
                          this.promptForCancellation();
                      }}
                  >
                  </tm-file-format-designer>`}</tm-section
        >`;
    }

    private async getWireUploadFormatData() {
        this.loading = true;
        try {
            await Promise.all([this.getEntitlements(), this.getFields()]);
            await this.getFormat(this.formatId);
        } catch (e: unknown) {
            this.notificationService.renderError(e as TmApiError);
        } finally {
            this.loading = false;
        }
    }

    private async getEntitlements() {
        this.entitlement = await this.entitlementsService.isEnabled(
            Entitlements.ManageWireFileFormats
        );
    }

    /**
     *
     * @param {int} id : the format Id from the link row selected or id in url path
     */
    private async getFormat(id: string) {
        if (id === 'new-format') {
            this.format = defaultFormat(this.formatFields);
            return;
        }
        try {
            this.format = await this.wiresService.fetchWireUploadFormat(
                Number(id),
                this.formatFields
            );
        } catch (e) {
            this.notificationService.renderError(e as TmApiError);
        }
    }

    /**
     * fetches the format fields used to build the tiles and descriptions in
     * the template formatter
     */
    private async getFields() {
        const fields = await this.wiresService.getWireUploadFormatFields();
        this.glossary = fields.glossary;
        this.formatFields = fields.tiles;
    }

    private async promptForCancellation() {
        const { reason } = await this.dialogService.open(
            'Are you sure you want to cancel? All unsaved changes will be lost.',
            'Cancel Changes',
            {
                buttons: {
                    [TmDialogButtonPosture.Primary]: {
                        label: 'Confirm',
                    },

                    [TmDialogButtonPosture.Secondary]: {
                        label: 'Cancel',
                    },
                },
            }
        );
        if (reason === 'confirm') {
            this.navService.navigate(`wires/files`);
        } else {
            return;
        }
    }

    private async saveFormat() {
        this.loading = true;
        if (this.formatId === 'new-format') {
            try {
                await this.wiresService.createWireUploadFormat(this.format);
                this.notificationService.renderSuccess('Format created successfully.');
                this.navService.navigate(`wires/files`);
            } catch (e) {
                this.notificationService.renderError(e as TmApiError);
            } finally {
                this.loading = false;
            }
        } else {
            try {
                await this.wiresService.updateWireUploadFormat(this.format);
                this.notificationService.renderSuccess('Format updated successfully.');
            } catch (e) {
                this.notificationService.renderError(e as TmApiError);
            } finally {
                this.loading = false;
            }
        }
    }

    public static get styles() {
        return [
            fontAwesome,
            css`
                :host {
                    height: 100%;
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: WireUploadFormatContainer;
    }
}
